import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { crmService } from '@services/crmService';
import { User } from '../../types/crm';

const Container = styled.div`
  max-width: 520px;
  margin: 0 auto;
  padding: 2rem 1rem 3rem;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
  padding: 1.75rem;
  border: 1px solid #e5e7eb;
`;

const Title = styled.h1`
  font-size: 1.4rem;
  color: #111827;
  margin: 0 0 0.5rem;
`;

const Subtitle = styled.p`
  color: #6b7280;
  margin: 0 0 1.25rem;
  font-size: 0.95rem;
`;

const Field = styled.div`
  margin-bottom: 0.85rem;
`;

const Label = styled.div`
  font-size: 0.8rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  margin-bottom: 0.35rem;
`;

const Value = styled.div`
  font-size: 1rem;
  color: #111827;
  font-weight: 600;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 0.85rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.2s;
  &:focus {
    outline: none;
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 0.5rem;
`;

const Button = styled.button<{ variant?: 'secondary' }>`
  flex: 1;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  border: ${({ variant }) => (variant ? '1px solid #d1d5db' : 'none')};
  background: ${({ variant }) => (variant ? '#fff' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)')};
  color: ${({ variant }) => (variant ? '#111827' : '#fff')};
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.1s ease, box-shadow 0.15s ease;
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.08);
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Message = styled.div<{ kind: 'error' | 'success' }>`
  margin-top: 0.5rem;
  padding: 0.75rem 0.9rem;
  border-radius: 8px;
  border: 1px solid ${({ kind }) => (kind === 'error' ? '#fecaca' : '#a7f3d0')};
  background: ${({ kind }) => (kind === 'error' ? '#fee2e2' : '#d1fae5')};
  color: ${({ kind }) => (kind === 'error' ? '#b91c1c' : '#065f46')};
  font-size: 0.92rem;
`;

export default function Account() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [changing, setChanging] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const getErrorMessage = (err: unknown, fallback: string) => {
    if (err && typeof err === 'object' && 'response' in err && err.response && typeof err.response === 'object' && 'data' in err.response && (err as any).response.data?.error) {
      return (err as any).response.data.error;
    }
    if (err instanceof Error) return err.message;
    return fallback;
  };

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await crmService.getCurrentUser();
        setUser(resp.user);
      } catch (err: unknown) {
        setError(getErrorMessage(err, 'Failed to load user'));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!user) {
      setError('You must be logged in.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }

    setChanging(true);
    try {
      const resp = await crmService.changePassword(currentPassword, newPassword);
      setSuccess(resp.message || 'Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Failed to change password'));
    } finally {
      setChanging(false);
    }
  };

  const handleLogout = () => {
    crmService.logout();
    window.location.href = '/admin/login';
  };

  return (
    <Container>
      <Card>
        <Title>Account</Title>
        <Subtitle>Manage your credentials and sign out.</Subtitle>

        {loading && <Subtitle>Loading user…</Subtitle>}
        {error && <Message kind="error">{error}</Message>}
        {user && !loading && (
          <>
            <Field>
              <Label>Username</Label>
              <Value>{user.username}</Value>
            </Field>
            <Field>
              <Label>Email</Label>
              <Value>{user.email || '—'}</Value>
            </Field>

            <Subtitle style={{ marginTop: '1rem' }}>Change Password</Subtitle>
            <Form onSubmit={handleChangePassword}>
              <Input
                type="password"
                placeholder="Current password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
              <Input
                type="password"
                placeholder="New password (min 8 chars)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
              <Input
                type="password"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                minLength={8}
                required
              />
              <Button type="submit" disabled={changing}>
                {changing ? 'Updating...' : 'Update Password'}
              </Button>
            </Form>
            {success && <Message kind="success">{success}</Message>}
            {error && !success && <Message kind="error">{error}</Message>}

            <Subtitle style={{ marginTop: '1.25rem' }}>Session</Subtitle>
            <ButtonRow>
              <Button variant="secondary" onClick={handleLogout}>Logout</Button>
            </ButtonRow>
          </>
        )}
      </Card>
    </Container>
  );
}
